import { useState, type CSSProperties, type ReactNode } from "react";
import { Bell, BellOff, CircleCheckBig, CircleDot, X } from "lucide-react";
import DateSplitInput from "@/components/foundations/inputs/DateSplitInput/DateSplitInput";
import DurationSlider from "@/components/foundations/inputs/DurationSlider/DurationSlider";
import {
  resolveTimePickerDateKey,
  StartStopTimeDialog,
  type StartStopTarget,
} from "@/components/tasks/timing/StartStopTimeDialog/StartStopTimeDialog";
import { TimeInputField } from "@/components/foundations/inputs/TimeInputField/TimeInputField";
import { Label } from "@/components/ui/label";
import {
  useTimePickerIncrement,
  type TimePickerIncrement,
} from "@/hooks/useTimePickerIncrement";
import { formatDateKey } from "@/lib/time";
import {
  setTaskTimingDuration,
  setTaskTimingEnd,
  setTaskTimingEndDate,
  setTaskTimingStart,
  setTaskTimingStartDate,
  type TaskTimingValue,
} from "@/lib/taskTiming";

export type TaskTimingColors = { bar: string; soft: string; ink: string };

export type { TaskTimingValue } from "@/lib/taskTiming";

export type TaskTimingChangeReason =
  | "start-date"
  | "end-date"
  | "start-time"
  | "end-time"
  | "duration";

export type TaskTimingBinding = {
  value: TaskTimingValue;
  onChange: (value: TaskTimingValue, reason: TaskTimingChangeReason) => void;
  onDurationBlur?: () => void;
  onStartTimeClick?: () => void;
  onEndTimeClick?: () => void;
};

export type TaskTimingFieldsProps = TaskTimingBinding & {
  alertEnabled?: boolean;
  onAlertEnabledChange?: (value: boolean) => void;
  colors: TaskTimingColors;
  minuteIncrement?: TimePickerIncrement;
};

export default function TaskTimingFields({
  value,
  onChange,
  onDurationBlur,
  onStartTimeClick,
  onEndTimeClick,
  alertEnabled = false,
  onAlertEnabledChange,
  colors,
  minuteIncrement,
}: TaskTimingFieldsProps) {
  const preferredMinuteIncrement = useTimePickerIncrement();
  const resolvedMinuteIncrement = minuteIncrement ?? preferredMinuteIncrement;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTarget, setDialogTarget] = useState<StartStopTarget>("start");
  const dialogDate = resolveTimePickerDateKey(value.startDate);
  const currentEndDate = value.endDate || value.startDate;
  const splitDate = Boolean(
    value.startDate && currentEndDate && value.startDate !== currentEndDate
  );
  const style = {
    "--section-bar": colors.bar,
    "--section-soft": colors.soft,
    "--section-ink": colors.ink,
  } as CSSProperties;

  function changeStartTimeAtDate(nextDate: string, nextTime: string) {
    const resolvedDate =
      nextDate || (nextTime ? formatDateKey(new Date()) : "");
    onChange(setTaskTimingStart(value, resolvedDate, nextTime), "start-time");
  }

  function changeEndTime(nextTime: string, nextDate = currentEndDate) {
    const resolvedStartDate =
      value.startDate || (nextTime ? formatDateKey(new Date()) : "");
    const resolvedEndDate = nextDate || resolvedStartDate;
    const nextValue =
      resolvedStartDate && !value.startDate
        ? { ...value, startDate: resolvedStartDate, endDate: resolvedEndDate }
        : value;
    onChange(
      setTaskTimingEnd(nextValue, resolvedEndDate, nextTime),
      "end-time"
    );
  }

  function changeDuration(durationMinutes: string) {
    onChange(setTaskTimingDuration(value, durationMinutes), "duration");
  }

  function openTimeDialog(target: StartStopTarget) {
    setDialogTarget(target);
    setDialogOpen(true);
  }

  return (
    <>
      <div className="timing-fields" style={style}>
        <DateSplitInput
          startDate={value.startDate}
          endDate={currentEndDate}
          startTime={value.startTime}
          endTime={value.endTime}
          onStartDateChange={startDate => {
            onChange(
              setTaskTimingStartDate(value, startDate, splitDate),
              "start-date"
            );
          }}
          onEndDateChange={endDate => {
            onChange(setTaskTimingEndDate(value, endDate), "end-date");
          }}
          onEndTimeChange={changeEndTime}
        />

        <div className="timing-time-fields">
          <TimeField
            label="Start"
            value={value.startTime}
            onChange={next => changeStartTimeAtDate(value.startDate, next)}
            onClear={() => changeStartTimeAtDate(value.startDate, "")}
            onClick={onStartTimeClick ?? (() => openTimeDialog("start"))}
            colors={colors}
            icon={<CircleDot />}
            alarm={alertEnabled}
            onAlarmChange={onAlertEnabledChange}
          />
          <TimeField
            label="End"
            value={value.endTime}
            onChange={changeEndTime}
            onClear={() => changeEndTime("")}
            onClick={onEndTimeClick ?? (() => openTimeDialog("end"))}
            colors={colors}
            icon={<CircleCheckBig />}
          />
        </div>

        <DurationSlider
          value={value.durationMinutes}
          onChange={changeDuration}
          onBlur={onDurationBlur}
          minuteIncrement={resolvedMinuteIncrement}
        />
      </div>

      <StartStopTimeDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initialTarget={dialogTarget}
        dateKey={dialogDate}
        endDateKey={currentEndDate}
        splitDate={splitDate}
        scheduledTime={value.startTime || null}
        startedAt={
          value.startTime ? combineDateTime(dialogDate, value.startTime) : null
        }
        completedAt={
          value.endTime
            ? combineDateTime(currentEndDate || dialogDate, value.endTime)
            : null
        }
        colors={colors}
        onConfirm={({ target, when, dateKey: confirmedDate }) => {
          if (!when) {
            if (target === "start") changeStartTimeAtDate(value.startDate, "");
            else changeEndTime("");
            return;
          }

          const nextDate = confirmedDate || formatDateKey(when);
          const nextTime = `${String(when.getHours()).padStart(2, "0")}:${String(when.getMinutes()).padStart(2, "0")}`;
          if (target === "start") changeStartTimeAtDate(nextDate, nextTime);
          else changeEndTime(nextTime, nextDate);
        }}
      />
    </>
  );
}

function TimeField({
  label,
  value,
  onChange,
  onClear,
  onClick,
  colors,
  icon,
  alarm = false,
  onAlarmChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  onClick: () => void;
  colors: TaskTimingColors;
  icon: ReactNode;
  alarm?: boolean;
  onAlarmChange?: (value: boolean) => void;
}) {
  return (
    <div className="field">
      <div className="field-header">
        <Label className="label">{label}</Label>
        {value && <ClearButton label={label.toLowerCase()} onClick={onClear} />}
      </div>
      <div className="time-row">
        <TimeInputField
          value={value}
          onChange={onChange}
          onClick={onClick}
          borderColor={colors.bar}
          leadingIcon={icon}
          use24Hour
        />
        {onAlarmChange && (
          <button
            type="button"
            className="alarm"
            aria-label={alarm ? "Turn alarm off" : "Turn alarm on"}
            title={alarm ? "Turn alarm off" : "Turn alarm on"}
            onClick={() => onAlarmChange(!alarm)}
          >
            {alarm ? <Bell /> : <BellOff />}
          </button>
        )}
      </div>
    </div>
  );
}

function combineDateTime(date: string, time: string) {
  const [year, month, day] = date.split("-").map(Number);
  const [hours, minutes] = time.split(":").map(Number);
  return new Date(year, month - 1, day, hours, minutes);
}

function ClearButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="clear"
      aria-label={`Clear ${label}`}
      title={`Clear ${label}`}
      onClick={onClick}
    >
      <X />
    </button>
  );
}
